// src/pages/AdminPage.jsx
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { RateLimitBanner, ConfirmModal } from '../components';
import { API_LIMITS } from '../lib/supabase';

async function adminAction(action, payload) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/admin-users`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`,
        'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY
      },
      body: JSON.stringify({ action, ...payload })
    }
  );
  const data = await res.json();
  if (!res.ok || data.error) throw new Error(data.error || 'Unknown error');
  return data;
}

export default function AdminPage() {
  const { profile, isOwner } = useAuth();
  const [users, setUsers] = useState([]);
  const [sleeves, setSleeves] = useState([]);
  const [apiLog, setApiLog] = useState([]);
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePassword, setInvitePassword] = useState('');
  const [inviteFullName, setInviteFullName] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviteMsg, setInviteMsg] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [sleeveName, setSleeveName] = useState('');
  const [sleeveBench, setSleeveBench] = useState('SPY');
  const [sleeveMsg, setSleeveMsg] = useState('');
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteSleeve, setDeleteSleeve] = useState(null);
  const [actionMsg, setActionMsg] = useState('');

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    const [{ data: u }, { data: s }, { data: l }] = await Promise.all([
      supabase.from('user_profiles').select('*').order('created_at'),
      supabase.from('sleeves').select('*').order('name'),
      supabase.from('api_call_log').select('*').order('created_at', { ascending: false }).limit(50)
    ]);
    setUsers(u || []);
    setSleeves(s || []);
    setApiLog(l || []);
    setLoading(false);
  }

  function flash(setter, msg) { setter(msg); setTimeout(() => setter(''), 4000); }

  async function handleCreateUser() {
    if (!inviteEmail || !invitePassword) return;
    setInviteLoading(true);
    try {
      await adminAction('create_user', { email: inviteEmail, password: invitePassword, full_name: inviteFullName, role: inviteRole });
      flash(setInviteMsg, `✓ Created ${inviteEmail} as ${inviteRole}`);
      setInviteEmail(''); setInvitePassword(''); setInviteFullName('');
      loadData();
    } catch (e) { flash(setInviteMsg, `✗ ${e.message}`); }
    setInviteLoading(false);
  }

  async function handleUpdateRole(userId, newRole) {
    try {
      await adminAction('update_role', { target_user_id: userId, new_role: newRole });
      flash(setActionMsg, '✓ Role updated');
      loadData();
    } catch (e) { flash(setActionMsg, `✗ ${e.message}`); }
  }

  async function handleDeleteUser() {
    try {
      await adminAction('delete_user', { target_user_id: deleteUser });
      flash(setActionMsg, '✓ User removed');
      loadData();
    } catch (e) { flash(setActionMsg, `✗ ${e.message}`); }
    setDeleteUser(null);
  }

  async function handleCreateSleeve() {
    if (!sleeveName.trim()) return;
    await supabase.from('sleeves').insert({ name: sleeveName.trim(), benchmark: sleeveBench, created_by: profile?.id });
    flash(setSleeveMsg, `✓ Created sleeve "${sleeveName}"`);
    setSleeveName('');
    loadData();
  }

  async function handleDeleteSleeve() {
    await supabase.from('sleeves').update({ is_active: false }).eq('id', deleteSleeve);
    setDeleteSleeve(null);
    loadData();
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Admin</div>
          <div className="page-subtitle">User management, sleeves, and API usage</div>
        </div>
      </div>

      <div className="page-body section-gap">
        <div className="tabs">
          {['users', 'sleeves', 'api_usage'].map(t => (
            <button key={t} className={`tab ${activeTab===t?'active':''}`} onClick={()=>setActiveTab(t)}>
              {t.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}
            </button>
          ))}
        </div>

        {activeTab === 'users' && (
          <div className="section-gap">
            {actionMsg && <div className={`alert ${actionMsg.startsWith('✓')?'alert-success':'alert-warning'}`}>{actionMsg}</div>}

            {isOwner && (
              <div className="card">
                <div className="card-header"><span className="card-title">Create New User</span></div>
                <div className="card-body">
                  {inviteMsg && <div className={`alert ${inviteMsg.startsWith('✓')?'alert-success':'alert-warning'}`} style={{marginBottom:12}}>{inviteMsg}</div>}
                  <div className="form-row">
                    <div className="form-group" style={{marginBottom:0}}>
                      <label className="form-label">Email *</label>
                      <input className="form-input" type="email" value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)} placeholder="user@fund.com" />
                    </div>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label className="form-label">Full Name</label>
                      <input className="form-input" value={inviteFullName} onChange={e=>setInviteFullName(e.target.value)} placeholder="Jane Smith" />
                    </div>
                  </div>
                  <div className="form-row" style={{marginTop:10}}>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label className="form-label">Temporary Password *</label>
                      <input className="form-input" type="password" value={invitePassword} onChange={e=>setInvitePassword(e.target.value)} placeholder="Min 6 characters" />
                    </div>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label className="form-label">Role</label>
                      <select className="form-select" value={inviteRole} onChange={e=>setInviteRole(e.target.value)}>
                        <option value="viewer">Viewer — view only</option>
                        <option value="admin">Admin — can edit portfolio</option>
                        <option value="owner">Owner — full control</option>
                      </select>
                    </div>
                  </div>
                  <button className="btn btn-primary" style={{marginTop:14}} onClick={handleCreateUser} disabled={!inviteEmail||!invitePassword||inviteLoading}>
                    {inviteLoading ? 'Creating...' : 'Create User'}
                  </button>
                  <div style={{fontSize:11,color:'var(--text-muted)',marginTop:8}}>User can log in immediately with the temporary password.</div>
                </div>
              </div>
            )}

            <div className="card">
              <div className="card-header">
                <span className="card-title">Users ({users.length})</span>
                <button className="btn btn-secondary btn-sm" onClick={loadData}>↺ Refresh</button>
              </div>
              <div className="table-container">
                <table>
                  <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Created</th>{isOwner&&<th>Actions</th>}</tr></thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id}>
                        <td className="primary">{u.email}</td>
                        <td style={{color:'var(--text-secondary)'}}>{u.full_name||'—'}</td>
                        <td>
                          {isOwner && u.id !== profile?.id ? (
                            <select className="form-select" style={{width:'auto',padding:'3px 8px',fontSize:11}} value={u.role} onChange={e=>handleUpdateRole(u.id,e.target.value)}>
                              <option value="viewer">Viewer</option>
                              <option value="admin">Admin</option>
                              <option value="owner">Owner</option>
                            </select>
                          ) : (
                            <span className={`badge badge-${u.role}`}>{u.role}</span>
                          )}
                        </td>
                        <td style={{color:'var(--text-muted)'}}>{new Date(u.created_at).toLocaleDateString()}</td>
                        {isOwner && (
                          <td>{u.id !== profile?.id && <button className="btn btn-danger btn-sm" onClick={()=>setDeleteUser(u.id)}>Remove</button>}</td>
                        )}
                      </tr>
                    ))}
                    {!users.length && <tr><td colSpan={5} style={{textAlign:'center',color:'var(--text-muted)',padding:24}}>No users found.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'sleeves' && (
          <div className="section-gap">
            <div className="card">
              <div className="card-header"><span className="card-title">New Sleeve</span></div>
              <div className="card-body">
                {sleeveMsg && <div className="alert alert-success">{sleeveMsg}</div>}
                <div className="form-row">
                  <div className="form-group" style={{marginBottom:0}}>
                    <label className="form-label">Sleeve Name</label>
                    <input className="form-input" value={sleeveName} onChange={e=>setSleeveName(e.target.value)} placeholder="e.g. Long/Short Equity" onKeyDown={e=>e.key==='Enter'&&handleCreateSleeve()} />
                  </div>
                  <div className="form-group" style={{marginBottom:0}}>
                    <label className="form-label">Benchmark</label>
                    <select className="form-select" value={sleeveBench} onChange={e=>setSleeveBench(e.target.value)}>
                      <option value="SPY">SPY (S&P 500)</option>
                      <option value="QQQ">QQQ (Nasdaq)</option>
                      <option value="IWM">IWM (Russell 2000)</option>
                      <option value="AGG">AGG (Bonds)</option>
                      <option value="CASH">Cash</option>
                    </select>
                  </div>
                </div>
                <button className="btn btn-primary" style={{marginTop:12}} onClick={handleCreateSleeve} disabled={!sleeveName.trim()}>Create Sleeve</button>
              </div>
            </div>
            <div className="card">
              <div className="card-header"><span className="card-title">Sleeves ({sleeves.length})</span></div>
              <div className="table-container">
                <table>
                  <thead><tr><th>Name</th><th>Benchmark</th><th>Status</th><th>Created</th><th></th></tr></thead>
                  <tbody>
                    {sleeves.map(s => (
                      <tr key={s.id}>
                        <td className="primary">{s.name}</td>
                        <td className="accent">{s.benchmark}</td>
                        <td><span className={`badge ${s.is_active?'badge-long':'badge-viewer'}`}>{s.is_active?'Active':'Inactive'}</span></td>
                        <td>{new Date(s.created_at).toLocaleDateString()}</td>
                        <td>{s.is_active&&<button className="btn btn-danger btn-sm" onClick={()=>setDeleteSleeve(s.id)}>Deactivate</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'api_usage' && (
          <div className="section-gap">
            <div className="card">
              <div className="card-header"><span className="card-title">Daily API Usage</span></div>
              <div className="card-body section-gap">
                {Object.entries(API_LIMITS).map(([k,v]) => (
                  <div key={k}>
                    <RateLimitBanner provider={k} />
                    <div style={{fontSize:11,color:'var(--text-muted)',marginTop:2,fontFamily:'var(--font-mono)'}}>{v.description}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="card">
              <div className="card-header"><span className="card-title">Recent API Calls (last 50)</span></div>
              <div className="table-container">
                <table>
                  <thead><tr><th>Time</th><th>Provider</th><th>Type</th><th>Calls Used</th><th>Status</th><th>By</th></tr></thead>
                  <tbody>
                    {apiLog.map(l => (
                      <tr key={l.id}>
                        <td style={{fontSize:10,color:'var(--text-muted)'}}>{new Date(l.created_at).toLocaleString()}</td>
                        <td className="accent" style={{textTransform:'capitalize'}}>{l.api_provider?.replace(/_/g,' ')}</td>
                        <td style={{fontSize:11}}>{l.call_type}</td>
                        <td>{l.calls_used}</td>
                        <td><span className={`badge ${l.status==='success'?'badge-long':l.status==='error'?'badge-short':'badge-warning'}`}>{l.status}</span></td>
                        <td style={{fontSize:10,color:'var(--text-muted)'}}>{l.called_by?.slice(0,8)}…</td>
                      </tr>
                    ))}
                    {!apiLog.length&&<tr><td colSpan={6} style={{textAlign:'center',color:'var(--text-muted)',padding:24}}>No API calls logged yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal isOpen={!!deleteUser} title="Remove User" message="This will permanently delete the user's account. This cannot be undone." onConfirm={handleDeleteUser} onCancel={()=>setDeleteUser(null)} danger />
      <ConfirmModal isOpen={!!deleteSleeve} title="Deactivate Sleeve" message="This will deactivate the sleeve. Existing positions will be retained but hidden." onConfirm={handleDeleteSleeve} onCancel={()=>setDeleteSleeve(null)} danger />
    </div>
  );
}
